import { useCallback, useState } from 'react';
import { PDPSectionType, useGetProductCatalogCollectionDisplayInfosByRefsLazyQuery as useBaseLazyQuery } from '@graphql/generated';
import { CatalogCollectionProductItemDetails } from '../../../Catalog.types';
import { notNullOrUndefined } from '@shared/utils/notNullOrUndefined';

export type UseGetPdpProductCollectionByIdArgs = {
  collectionsRefList: CollectionsRef[];
};

export type CollectionsRef = {
  displayInfoId: string;
  pdpSectionType: string;
};

export type Collection = {
  id: string;
  title: string;
  pdpSectionType: Maybe<PDPSectionType>;
  items: Maybe<CatalogCollectionProductItemDetails[]>;
};

export const useGetPdpProductCollectionById = ({ selectedProductId }: { selectedProductId?: string }) => {
  const [collectionsList, setCollectionsList] = useState<Collection[]>([]);

  const [getCollections, { error, loading }] = useBaseLazyQuery({
    batchMode: 'fast',
    // To avoid apollo cache entity conflicts, don't store the result of this query.
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'no-cache',
    onCompleted: data => {
      const _collectionsList = data.productCatalogCollectionDisplayInfosByRefs.map(_collection => ({
        id: _collection?.displayInfoId ?? '',
        title: _collection?.title ?? '',
        pdpSectionType: _collection?.pdpSectionType,
        items: _collection?.collection?.members
          ?.map(item => {
            if (item.__typename !== 'ProductCatalogEntityItem') return null;
            if (selectedProductId && item.id === selectedProductId) return null;
            return item.catalogItemData as CatalogCollectionProductItemDetails;
          })
          .filter(notNullOrUndefined)
      }));
      setCollectionsList(_collectionsList);
    }
  });

  const fetchCollections = useCallback(
    (args: UseGetPdpProductCollectionByIdArgs) => {
      getCollections({
        variables: args
      });
    },
    [getCollections]
  );

  return {
    collectionsList,
    fetchCollections,
    error,
    loading
  };
};
